import { useEffect, useState, useRef, useCallback, memo } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import WorkerBadge from "../components/WorkerBadge";
import { Megaphone, Send, Star, CheckCircle2, Lock, Unlock, Flag, ArrowLeft, UserPlus, AlertTriangle, Trash2, Pencil, X, Shield, ChevronUp, ChevronDown } from "lucide-react";
import QRAttendance from "../components/QRAttendance";

// Pure, stable helpers — module-level so they never create a new function
// reference on render, which would otherwise defeat row memoization below.
function formatTime(iso) {
  return iso ? new Date(iso).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" }) : null;
}
function getDelayMinutes(checkInTime, eventDate) {
  if (!checkInTime || !eventDate) return null;
  const diffMs = new Date(checkInTime) - new Date(eventDate);
  const diffMin = Math.round(diffMs / 60000);
  return diffMin > 0 ? diffMin : null;
}

// Merge poll/refetch results into existing state, keeping object references stable
// for unchanged rows so React.memo can actually skip re-rendering them.
// skipIds: rows with an in-flight mutation — never let a poll clobber those.
function mergeApps(prevApps, nextApps, skipIds) {
  const prevById = new Map(prevApps.map(a => [a.id, a]));
  let changed = prevApps.length !== nextApps.length;
  const merged = nextApps.map(next => {
    const prev = prevById.get(next.id);
    if (skipIds && skipIds.has(next.id) && prev) {
      return prev; // action in flight for this row — don't let a poll overwrite it
    }
    if (prev && JSON.stringify(prev) === JSON.stringify(next)) {
      return prev; // identical content -> same reference -> memoized row bails out
    }
    changed = true;
    return next;
  });
  return changed ? merged : prevApps;
}

// Job comparison for the poll — ignores QR fields, which churn every ~20s when
// auto-refresh is on. Nothing in this page's row rendering depends on them
// (QRAttendance manages its own QR state independently), so treating a QR-only
// change as "no change" avoids re-rendering every applicant row during a scan rush.
function jobChanged(prev, next) {
  if (!prev) return true;
  const strip = ({ qr_token, qr_expires_at, qr_auto_refresh, ...rest }) => rest;
  return JSON.stringify(strip(prev)) !== JSON.stringify(strip(next));
}

export default function ManageEvent() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const fromEvent = searchParams.get("from") === "event";
  const fromDashboard = searchParams.get("from") === "dashboard";
  const eventId = searchParams.get("event_id");
  const [job, setJob] = useState(null);
  const [apps, setApps] = useState([]);
  const [newSlots, setNewSlots] = useState("");
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcastMsg, setBroadcastMsg] = useState("");
  const [sendingBroadcast, setSendingBroadcast] = useState(false);
  const [showReviewPrompt, setShowReviewPrompt] = useState(false);

  // Manual add state
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [addPhone, setAddPhone] = useState("");
  const [addingManual, setAddingManual] = useState(false);

  // Captain state
  const [captainOpen, setCaptainOpen] = useState(false);
  const [captainSearch, setCaptainSearch] = useState("");
  const [captainSuggestions, setCaptainSuggestions] = useState([]);
  const [searchingCaptains, setSearchingCaptains] = useState(false);
  const [previousCaptains, setPreviousCaptains] = useState([]);
  const [selectedCaptain, setSelectedCaptain] = useState(null);
  const [captainCanMarkPayment, setCaptainCanMarkPayment] = useState(false);
  const [addingCaptain, setAddingCaptain] = useState(false);

  // Slots full warning state
  const [slotWarningOpen, setSlotWarningOpen] = useState(false);

  // Cancel / delete state
  const [cancelPopupOpen, setCancelPopupOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Rate employee state
  const [rateApp, setRateApp] = useState(null); // application being rated
  const [rateStars, setRateStars] = useState({}); // { categoryName: stars }
  const [ratingSaving, setRatingSaving] = useState(false);
  const slotsEditingRef = useRef(false);

  // In-flight action guard — one mutation per applicant at a time. Prevents
  // duplicate submits on double-clicks AND prevents two different actions on
  // the same row from racing each other's server responses.
  const pendingRef = useRef(new Set());
  const [pendingIds, setPendingIds] = useState(() => new Set());
  const markPending = (aid, isPending) => {
    if (isPending) pendingRef.current.add(aid);
    else pendingRef.current.delete(aid);
    setPendingIds(new Set(pendingRef.current));
  };

  // Latest apps/job without forcing handler functions to be re-created every render —
  // keeps handler references stable so memoized rows don't lose their memoization.
  const appsRef = useRef(apps);
  useEffect(() => { appsRef.current = apps; }, [apps]);
  const jobRef = useRef(job);
  useEffect(() => { jobRef.current = job; }, [job]);

  const load = async () => {
    const { data: j } = await api.get(`/jobs/${id}`);
    setJob(prev => (jobChanged(prev, j) ? j : prev));
    if (!slotsEditingRef.current) setNewSlots(j.total_slots);
    const { data: a } = await api.get(`/jobs/${id}/applications`);
    setApps(prev => mergeApps(prev, a, pendingRef.current));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Poll every 5 seconds
  useEffect(() => {
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [id]);

  // Refresh when tab gets focus
  useEffect(() => {
    const handleFocus = () => load();
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
    // eslint-disable-next-line
  }, []);

  // Optimistic attendance/status update — UI reflects the change instantly,
  // the network call reconciles exact fine/points math in the background.
  const updateApp = useCallback(async (aid, payload) => {
    if (pendingRef.current.has(aid)) return;

    const currentApps = appsRef.current;
    const currentJob = jobRef.current;

    if (payload.status === "confirmed") {
      const confirmedCount = currentApps.filter(a => a.status === "confirmed").length;
      if (confirmedCount >= (currentJob?.total_slots || 0)) {
        setSlotWarningOpen(true);
        return;
      }
    }

    markPending(aid, true);

    const prevApp = currentApps.find(a => a.id === aid);
    const patch = {};
    if (payload.status) patch.status = payload.status;
    const now = new Date().toISOString();
    switch (payload.attendance_action) {
      case "check_in": patch.check_in_time = now; break;
      case "check_out": patch.check_out_time = now; patch.attendance = "present"; break;
      case "absent": patch.attendance = "absent"; break;
      case "reset": patch.check_in_time = null; patch.check_out_time = null; patch.attendance = null; patch.fine_amount = 0; break;
      default: break;
    }
    setApps(prev => prev.map(a => (a.id === aid ? { ...a, ...patch } : a)));

    try {
      await api.patch(`/applications/${aid}`, payload);
      const { data } = await api.get(`/jobs/${id}/applications`);
      // Merge, don't replace — keeps every other row's reference stable
      setApps(prev => mergeApps(prev, data));
      if (payload.attendance_action === "check_out") {
        const confirmed = data.filter(a => a.status === "confirmed");
        const allMarked = confirmed.length > 0 && confirmed.every(a => a.attendance === "present" || a.attendance === "absent");
        if (allMarked) setShowReviewPrompt(true);
      }
    } catch (e) {
      setApps(prev => prev.map(a => (a.id === aid ? (prevApp || a) : a)));
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update");
    } finally {
      markPending(aid, false);
    }
  }, [id]);

  const openRateModal = useCallback((app) => {
    const initial = {};
    const existing = {};
    (app.fine_ratings || []).forEach(r => { existing[r.category] = r.stars; });
    (jobRef.current?.fine_policy?.categories || []).forEach(c => { initial[c.name] = existing[c.name] ?? 5; });
    setRateStars(initial);
    setRateApp(app);
  }, []);

  const submitRating = async () => {
    setRatingSaving(true);
    try {
      const ratings = Object.entries(rateStars).map(([category, stars]) => ({ category, stars }));
      const { data } = await api.post(`/applications/${rateApp.id}/rate`, { ratings });
      toast.success(data.total_fine > 0 ? `Rated — total fine: ₹${data.total_fine}` : "Rated — no fines!");
      setRateApp(null);
      const updatedApps = await api.get(`/jobs/${id}/applications`);
      setApps(prev => mergeApps(prev, updatedApps.data));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRatingSaving(false); }
  };

  // Optimistic removal — row disappears instantly, restored if the delete fails.
  const removeApplicant = useCallback(async (aid, name) => {
    if (pendingRef.current.has(aid)) return;
    if (!window.confirm(`Remove ${name} from this event? This cannot be undone.`)) return;
    markPending(aid, true);
    const prevApps = appsRef.current;
    setApps(prev => prev.filter(a => a.id !== aid));
    try {
      await api.delete(`/applications/${aid}`);
      toast.success("Removed");
    } catch (e) {
      setApps(prevApps);
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      markPending(aid, false);
    }
  }, []);

  // Optimistic payment toggle — flips the one field instantly, reverts on error.
  const togglePaid = useCallback(async (aid, current) => {
    if (pendingRef.current.has(aid)) return;
    markPending(aid, true);
    setApps(prev => prev.map(a => (a.id === aid ? { ...a, payment_paid: !current } : a)));
    try {
      await api.patch(`/applications/${aid}/payment`, { paid: !current });
    } catch (e) {
      setApps(prev => prev.map(a => (a.id === aid ? { ...a, payment_paid: current } : a)));
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update payment");
    } finally {
      markPending(aid, false);
    }
  }, []);

  const updateSlots = async () => {
    slotsEditingRef.current = true;
    await api.patch(`/jobs/${id}`, { total_slots: parseInt(newSlots) });
    toast.success("Slots updated");
    await load();
    slotsEditingRef.current = false;
  };

  const closeHiring = async () => {
    await api.patch(`/jobs/${id}`, { status: "closed" });
    toast.success("Hiring closed — post hidden from job listings"); load();
  };

  const reopenHiring = async () => {
    await api.patch(`/jobs/${id}`, { status: "open" });
    toast.success("Hiring reopened — post visible again"); load();
  };

  const handleDeleteOrCancel = () => {
    if (apps.length === 0) {
      if (window.confirm("Delete this post? This cannot be undone.")) {
        deletePost();
      }
    } else {
      setCancelPopupOpen(true);
    }
  };

  const deletePost = async () => {
    try {
      await api.delete(`/jobs/${id}`);
      toast.success("Post deleted");
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    }
  };

  const cancelPost = async () => {
    setCancelling(true);
    try {
      await api.patch(`/jobs/${id}`, { status: "cancelled" });
      toast.success("Event cancelled — applicants have been notified");
      setCancelPopupOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setCancelling(false);
    }
  };

  const markFinished = async () => {
    await api.patch(`/jobs/${id}`, { status: "completed" });
    toast.success("Event marked as finished! Points updated for all workers."); load();
  };

  const sendBroadcast = async () => {
    const m = broadcastMsg.trim();
    if (!m) return;
    setSendingBroadcast(true);
    try {
      const { data } = await api.post(`/jobs/${id}/broadcast`, { message: m });
      toast.success(`Sent to ${data.sent} applicant${data.sent === 1 ? "" : "s"}`);
      setBroadcastMsg(""); setBroadcastOpen(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSendingBroadcast(false); }
  };

  // Add person manually — no account needed, just name + phone
  const addManual = async () => {
    if (!addName.trim() || !addPhone.trim()) {
      toast.error("Name and phone number are required");
      return;
    }
    setAddingManual(true);
    try {
      const confirmedCount = apps.filter(a => a.status === "confirmed").length;
      const status = confirmedCount < job.total_slots ? "confirmed" : "waitlist";
      await api.post(`/jobs/${id}/manual-add`, {
        name: addName.trim(),
        phone: addPhone.trim(),
        status,
      });
      toast.success(status === "confirmed"
        ? `${addName} added as confirmed`
        : `Slots full — ${addName} added to waitlist`
      );
      setAddName(""); setAddPhone(""); setAddOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setAddingManual(false);
    }
  };

  const openCaptainModal = async () => {
    setCaptainOpen(true);
    try {
      const { data } = await api.get("/users/previous-captains");
      setPreviousCaptains(data);
    } catch {}
  };

  const handleCaptainSearch = async (val) => {
    setCaptainSearch(val);
    setSelectedCaptain(null);
    setCaptainSuggestions([]);
    if (val.length < 2) return;
    setSearchingCaptains(true);
    try {
      const { data } = await api.get(`/users/search?q=${encodeURIComponent(val)}&limit=5&field=name`);
      setCaptainSuggestions(data);
    } catch {} finally { setSearchingCaptains(false); }
  };

  const selectCaptain = (u) => {
    setSelectedCaptain(u);
    setCaptainSearch(u.name);
    setCaptainSuggestions([]);
  };

  const addCaptain = async () => {
    if (!selectedCaptain) return;
    setAddingCaptain(true);
    try {
      await api.post(`/jobs/${id}/captains`, {
        user_id: selectedCaptain.id,
        can_mark_payment: captainCanMarkPayment,
      });
      toast.success(`${selectedCaptain.name} added as captain`);
      setCaptainOpen(false);
      setCaptainSearch(""); setSelectedCaptain(null); setCaptainCanMarkPayment(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setAddingCaptain(false);
    }
  };

  const removeCaptain = async (cuid, name) => {
    if (!window.confirm(`Remove ${name} as captain?`)) return;
    try {
      await api.delete(`/jobs/${id}/captains/${cuid}`);
      toast.success("Captain removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!job) return <div className="p-12 text-center text-muted-foreground">Loading…</div>;

  const confirmed = apps.filter(a => a.status === "confirmed");
  const waitlist = apps.filter(a => a.status === "waitlist");
  const presentCount = confirmed.filter(a => a.attendance === "present").length;
  const absentCount = confirmed.filter(a => a.attendance === "absent").length;
  const isClosed = job.status === "closed";
  const isCompleted = job.status === "completed";
  const isCancelled = job.status === "cancelled";

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-8" data-testid="manage-event-page">
      {/* Back button */}
      {fromEvent && eventId && (
        <Link to={`/manage-event/${eventId}`} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-brand mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to event
        </Link>
      )}
      {fromDashboard && (
        <Link to="/dashboard" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-brand mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </Link>
      )}

      <span className="label-eyebrow text-muted-foreground">Manage event</span>
      <div className="flex items-center gap-3 mt-2 flex-wrap">
        <h1 className="font-display text-3xl tracking-tighter">{job.title}</h1>
        {isClosed && (
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-300">
            🔒 Hiring Closed
          </span>
        )}
        {isCompleted && (
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-300">
            ✓ Event Finished
          </span>
        )}
        {isCancelled && (
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-600 border border-red-300">
            ✕ Cancelled
          </span>
        )}
      </div>
      <p className="text-sm text-slate-600 mt-1">{job.specialization} · {job.opportunity_type}</p>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat k="Confirmed" v={confirmed.length} />
        <Stat k="Waitlist" v={waitlist.length} />
        <Stat k="Present" v={presentCount} color="text-emerald-600" />
        <Stat k="Absent" v={absentCount} color="text-red-500" />
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        {job.viewer_can_manage && (
        <div className="flex flex-wrap gap-3 items-end card-flat">
          {!isCompleted && !isCancelled && (
            <>
              <div>
                <label className="label-eyebrow text-muted-foreground">Total slots</label>
                <input type="number" min={0} value={newSlots} onFocus={() => { slotsEditingRef.current = true; }} onBlur={() => { slotsEditingRef.current = false; }} onChange={e => setNewSlots(e.target.value)} className="input-base mt-2 max-w-[120px]" />
              </div>
              <button onClick={updateSlots} className="btn-primary">Update slots</button>
            </>
          )}
          <button onClick={() => setBroadcastOpen(true)} className="btn-tint">
            <Megaphone className="w-4 h-4" /> Broadcast
          </button>
          <button onClick={() => setAddOpen(true)} className="btn-tint">
            <UserPlus className="w-4 h-4" /> Add person
          </button>
          <button onClick={openCaptainModal} className="btn-tint">
            <Shield className="w-4 h-4" /> Add captain
          </button>
          {!isCompleted && !isCancelled && (
            <Link to={`/edit-job/${id}`} className="btn-outline flex items-center gap-1.5">
              <Pencil className="w-4 h-4" /> Edit
            </Link>
          )}
          {!isClosed && !isCompleted && !isCancelled && (
            <button onClick={closeHiring} className="btn-outline flex items-center gap-1.5">
              <Lock className="w-4 h-4" /> Close hiring
            </button>
          )}
          {isClosed && !isCompleted && !isCancelled && (
            <button onClick={reopenHiring} className="btn-outline flex items-center gap-1.5 !border-brand !text-brand">
              <Unlock className="w-4 h-4" /> Reopen hiring
            </button>
          )}
          {!isCompleted && !isCancelled && (
            <button onClick={markFinished} className="btn-outline flex items-center gap-1.5 !border-emerald-500 !text-emerald-600">
              <Flag className="w-4 h-4" /> Mark event finished
            </button>
          )}
          {!isCompleted && !isCancelled && (
            <button onClick={handleDeleteOrCancel} className="btn-outline flex items-center gap-1.5 !border-red-400 !text-red-500">
              <Trash2 className="w-4 h-4" /> {apps.length === 0 ? "Delete post" : "Cancel event"}
            </button>
          )}
        </div>
        )}
        {!isCompleted && !isCancelled && (job.viewer_can_manage || job.viewer_is_captain) && <QRAttendance jobId={id} />}
      </div>

      {job.viewer_can_manage && job.captains?.length > 0 && (
        <div className="mt-4 card-flat">
          <div className="flex items-center justify-between mb-2">
            <p className="label-eyebrow text-muted-foreground">Captains ({job.captains.length})</p>
            <button
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/manage/${id}`);
                toast.success("Captain link copied!");
              }}
              className="text-xs text-brand hover:underline"
            >
              Copy captain link
            </button>
          </div>
          <div className="space-y-2">
            {job.captains.map(c => (
              <div key={c.user_id} className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  {c.avatar
                    ? <img src={c.avatar} alt="" className="w-7 h-7 rounded-full object-cover" />
                    : <div className="w-7 h-7 rounded-full bg-brand-tint grid place-items-center text-xs font-bold text-brand">{(c.name || "?")[0]}</div>
                  }
                  <div>
                    <p className="text-sm font-medium text-foreground">{c.name}</p>
                    {c.can_mark_payment && <p className="text-[10px] text-emerald-600">Can mark payments</p>}
                  </div>
                </div>
                <button onClick={() => removeCaptain(c.user_id, c.name)} className="text-muted-foreground hover:text-red-500 transition p-1">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── CANCEL / DELETE CONFIRMATION POPUP ── */}
      {cancelPopupOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setCancelPopupOpen(false)}>
          <div className="bg-card rounded-2xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <h3 className="font-display text-lg mb-2 flex items-center gap-2 text-foreground">
              <AlertTriangle className="w-5 h-5 text-amber-500" /> Cancel this event?
            </h3>
            <p className="text-sm text-muted-foreground mb-5">
              {confirmed.length > 0
                ? `${confirmed.length} ${confirmed.length === 1 ? "person is" : "people are"} confirmed for this event. Are you sure you want to cancel? Confirmed and applied workers will be notified immediately.`
                : "Applicants will be notified that this event has been cancelled."}
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setCancelPopupOpen(false)} className="btn-outline">
                No, keep post
              </button>
              <button onClick={cancelPost} disabled={cancelling} className="btn-primary !bg-red-500 !border-red-500 hover:!bg-red-600">
                {cancelling ? "Cancelling…" : "Yes, cancel event"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── SLOTS FULL WARNING POPUP ── */}
      {slotWarningOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4">
          <div className="bg-card rounded-2xl p-6 max-w-sm w-full text-center shadow-2xl">
            <div className="w-14 h-14 rounded-full bg-amber-100 grid place-items-center mx-auto mb-4">
              <AlertTriangle className="w-7 h-7 text-amber-600" />
            </div>
            <h3 className="font-display text-xl mb-2">Slots are full</h3>
            <p className="text-sm text-muted-foreground mb-1">
              All <strong>{job.total_slots}</strong> slot{job.total_slots === 1 ? " is" : "s are"} already confirmed.
            </p>
            <p className="text-sm text-muted-foreground mb-5">
              To confirm this person, first increase the total slots using the <strong>Update slots</strong> field above.
            </p>
            <button
              onClick={() => setSlotWarningOpen(false)}
              className="btn-primary w-full"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* ── ATTENDANCE COMPLETE POPUP ── */}
      {showReviewPrompt && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4">
          <div className="bg-card rounded-2xl p-6 max-w-md w-full text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <h3 className="font-display text-xl mb-2">Attendance Complete!</h3>
            <p className="text-sm text-muted-foreground mb-4">All workers have been marked. Would you like to review your workers now?</p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => setShowReviewPrompt(false)} className="btn-outline">Later</button>
              <Link to={`/job/${id}`} onClick={() => setShowReviewPrompt(false)} className="btn-primary inline-flex items-center gap-2">
                <Star className="w-4 h-4" /> Review workers
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* ── BROADCAST POPUP ── */}
      {broadcastOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setBroadcastOpen(false)}>
          <div className="bg-card rounded-2xl p-6 max-w-lg w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <Megaphone className="w-5 h-5 text-brand" />
              <h3 className="font-display text-xl">Broadcast to applicants</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Sending to <strong>all {apps.length}</strong> applicant{apps.length === 1 ? "" : "s"}.</p>
            <textarea rows={5} className="input-base" placeholder="e.g. Reporting time changed to 5 PM. Wear black formals."
              value={broadcastMsg} onChange={e => setBroadcastMsg(e.target.value)} />
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={() => setBroadcastOpen(false)} className="btn-outline">Cancel</button>
              <button onClick={sendBroadcast} disabled={sendingBroadcast || !broadcastMsg.trim()} className="btn-primary">
                <Send className="w-4 h-4" /> {sendingBroadcast ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MANUAL ADD POPUP ── */}
      {addOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setAddOpen(false)}>
          <div className="bg-card rounded-2xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <UserPlus className="w-5 h-5 text-brand" />
              <h3 className="font-display text-xl">Add person manually</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              {apps.filter(a => a.status === "confirmed").length < job.total_slots
                ? "They will be added as confirmed."
                : <span className="text-amber-600 font-medium">Slots are full — they will be added to waitlist.</span>
              }
            </p>
            <div className="space-y-3">
              <div>
                <label className="label-eyebrow text-muted-foreground">Full name</label>
                <input
                  className="input-base mt-1"
                  placeholder="e.g. Rahul Nair"
                  value={addName}
                  onChange={e => setAddName(e.target.value)}
                />
              </div>
              <div>
                <label className="label-eyebrow text-muted-foreground">Phone number</label>
                <input
                  className="input-base mt-1"
                  placeholder="e.g. +91 9539XXXXXX"
                  value={addPhone}
                  onChange={e => setAddPhone(e.target.value)}
                  type="tel"
                />
              </div>
            </div>
            <div className="mt-5 flex gap-2 justify-end">
              <button onClick={() => { setAddOpen(false); setAddName(""); setAddPhone(""); }} className="btn-outline">Cancel</button>
              <button onClick={addManual} disabled={addingManual || !addName.trim() || !addPhone.trim()} className="btn-primary">
                {addingManual ? "Adding…" : "Add person"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD CAPTAIN POPUP ── */}
      {captainOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setCaptainOpen(false)}>
          <div className="bg-card rounded-2xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-5 h-5 text-brand" />
              <h3 className="font-display text-xl">Add captain</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Captains can generate QR codes and mark attendance for this role.</p>

            {previousCaptains.length > 0 && !selectedCaptain && (
              <div className="mb-4">
                <label className="label-eyebrow text-muted-foreground">Previous captains</label>
                <div className="flex flex-wrap gap-2 mt-1.5">
                  {previousCaptains.map(u => (
                    <button key={u.id} type="button" onClick={() => selectCaptain(u)}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-border hover:border-brand transition text-xs">
                      {u.avatar
                        ? <img src={u.avatar} alt="" className="w-5 h-5 rounded-full object-cover" />
                        : <div className="w-5 h-5 rounded-full bg-brand-tint grid place-items-center text-[10px] font-bold text-brand">{(u.name || "?")[0]}</div>
                      }
                      {u.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="relative">
              <label className="label-eyebrow text-muted-foreground">Search by name</label>
              <input className="input-base mt-1" placeholder="e.g. Rahul" value={captainSearch}
                onChange={e => handleCaptainSearch(e.target.value)} autoComplete="off" />
              {captainSuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-xl shadow-xl z-50 overflow-hidden">
                  {captainSuggestions.map(u => (
                    <button key={u.id} type="button" onClick={() => selectCaptain(u)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-brand-tint transition text-left">
                      {u.avatar
                        ? <img src={u.avatar} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                        : <div className="w-8 h-8 rounded-full bg-brand-tint grid place-items-center text-xs font-bold text-brand flex-shrink-0">{(u.name || "?")[0]}</div>
                      }
                      <div>
                        <p className="text-sm font-medium text-foreground">{u.name}</p>
                        {u.username && <p className="text-xs text-muted-foreground">@{u.username}</p>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {searchingCaptains && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-xl shadow-xl z-50 p-3 text-center text-xs text-muted-foreground">
                  Searching…
                </div>
              )}
            </div>

            <label className="flex items-center gap-2 mt-4 cursor-pointer select-none">
              <input type="checkbox" checked={captainCanMarkPayment} onChange={e => setCaptainCanMarkPayment(e.target.checked)} className="w-4 h-4" />
              <span className="text-sm text-foreground">Allow this captain to mark payments</span>
            </label>

            <div className="mt-5 flex gap-2 justify-end">
              <button onClick={() => { setCaptainOpen(false); setCaptainSearch(""); setSelectedCaptain(null); setCaptainCanMarkPayment(false); }} className="btn-outline">Cancel</button>
              <button onClick={addCaptain} disabled={addingCaptain || !selectedCaptain} className="btn-primary">
                {addingCaptain ? "Adding…" : "Add captain"}
              </button>
            </div>
          </div>
        </div>
      )}

      <Section
        title="Confirmed"
        data={confirmed}
        updateApp={updateApp}
        type="confirmed"
        isCompleted={isCompleted || isCancelled}
        job={job}
        openRateModal={openRateModal}
        removeApplicant={removeApplicant}
        togglePaid={togglePaid}
        pendingIds={pendingIds}
      />
      <Section
        title="Waitlist"
        data={waitlist}
        updateApp={updateApp}
        type="waitlist"
        isCompleted={isCompleted || isCancelled}
        job={job}
        openRateModal={openRateModal}
        removeApplicant={removeApplicant}
        togglePaid={togglePaid}
        pendingIds={pendingIds}
      />

      {/* ── RATE EMPLOYEE POPUP ── */}
      {rateApp && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setRateApp(null)}>
          <div className="bg-card rounded-2xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <h3 className="font-display text-xl mb-1">
              {rateApp.fine_ratings?.length > 0 ? "Edit rating for" : "Rate"} {rateApp.user_name}
            </h3>
            <p className="text-xs text-muted-foreground mb-4">5★ = no fine. Each missing star costs the configured rate.</p>
            <div className="space-y-4">
              {(job.fine_policy?.categories || []).map(cat => (
                <div key={cat.name}>
                  <p className="text-sm font-medium text-foreground mb-1">{cat.name} <span className="text-xs text-muted-foreground">(₹{cat.rate_per_star}/star)</span></p>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map(n => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setRateStars(s => ({ ...s, [cat.name]: n }))}
                        className={`text-2xl ${n <= (rateStars[cat.name] || 0) ? "text-amber-500" : "text-slate-300"}`}
                      >★</button>
                    ))}
                  </div>
                </div>
              ))}
              {(!job.fine_policy?.categories || job.fine_policy.categories.length === 0) && (
                <p className="text-sm text-muted-foreground">No star categories configured for this job.</p>
              )}
            </div>
            <div className="mt-5 flex gap-2 justify-end">
              <button onClick={() => setRateApp(null)} className="btn-outline">Cancel</button>
              <button onClick={submitRating} disabled={ratingSaving} className="btn-primary">
                {ratingSaving ? "Saving…" : "Submit rating"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ k, v, color = "text-foreground" }) {
  return (
    <div className="border border-border p-4 rounded-sm bg-background">
      <p className={`font-display text-3xl ${color}`}>{v}</p>
      <p className="text-xs text-muted-foreground">{k}</p>
    </div>
  );
}

function Section({ title, data, updateApp, type, isCompleted, job, openRateModal, removeApplicant, togglePaid, pendingIds }) {
  return (
    <section className="mt-8">
      <h2 className="font-display text-xl mb-3">{title} ({data.length})</h2>
      {data.length === 0 ? <p className="text-sm text-muted-foreground">None</p> : (
        <div className="space-y-2">
          {data.map(a => (
            <ApplicantRow
              key={a.id}
              a={a}
              type={type}
              isCompleted={isCompleted}
              job={job}
              openRateModal={openRateModal}
              removeApplicant={removeApplicant}
              togglePaid={togglePaid}
              updateApp={updateApp}
              isPending={pendingIds.has(a.id)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

// Memoized row — only re-renders when this specific applicant's data,
// pending state, or the job's viewer flags actually change.
const ApplicantRow = memo(function ApplicantRow({ a, type, isCompleted, job, openRateModal, removeApplicant, togglePaid, updateApp, isPending }) {
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [showTravelDetail, setShowTravelDetail] = useState(false);
  const busyClass = isPending ? "opacity-60" : "";
  const delayMinutes = getDelayMinutes(a.check_in_time, job.event_date);
  return (
    <div className="card-flat relative flex flex-wrap items-center gap-3 justify-between">
      {!isCompleted && job.viewer_can_manage && (
        <button
          onClick={() => removeApplicant(a.id, a.user_name)}
          title="Remove applicant"
          disabled={isPending}
          className={`absolute top-2 right-2 text-muted-foreground hover:text-red-500 transition p-1 ${busyClass}`}
        >
          <X className="w-4 h-4" />
        </button>
      )}
      <div className="flex items-center gap-3">
        {a.user_avatar
          ? <img src={a.user_avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
          : <div className="w-10 h-10 rounded-full bg-brand-tint grid place-items-center text-sm font-bold text-brand">
              {(a.user_name || "?")[0]}
            </div>
        }
                <div>
          <p className="text-sm font-medium text-foreground flex items-center gap-1.5">
            {a.user_name}
            <WorkerBadge tier={a.badge_tier} />
          </p>
          {a.user_username && (
            <p className="text-xs text-muted-foreground">@{a.user_username}</p>
          )}
          {/* Phone number — shown when available */}
          {a.user_phone && (
            <p className="text-xs text-brand mt-0.5">📞 {a.user_phone}</p>
          )}
          {a.user_points != null && (
            <p className="text-xs text-amber-500">⭐ {a.user_points} pts</p>
          )}
          {/* Manual entry badge */}
          {a.manual && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
              Manual entry
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1.5">
        {/* Attendance status badges */}
        {type === "confirmed" && (
          <div className="flex flex-wrap gap-1.5 items-center justify-end">
            {a.check_in_time && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-medium">
                In: {formatTime(a.check_in_time)}
              </span>
            )}
            {delayMinutes && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200 font-semibold">
                Delayed {delayMinutes} min
              </span>
            )}
            {a.check_out_time && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-medium">
                Out: {formatTime(a.check_out_time)}
              </span>
            )}
            {a.attendance === "present" && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">✓ Present</span>
            )}
            {a.attendance === "absent" && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-semibold">✗ Absent</span>
            )}
            {a.fine_amount > 0 && (
              <span
                className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200 font-semibold cursor-help"
                title={(a.fine_ratings || []).filter(r => r.fine > 0).map(r => `${r.category}: ${r.stars}★ (-₹${r.fine})`).join(" · ")}
              >
                Fine: ₹{a.fine_amount}
              </span>
            )}
          </div>
        )}

        <div className="flex flex-wrap gap-2 items-center">
          {!isCompleted && type === "waitlist" && job.viewer_can_manage && (
            <button onClick={() => updateApp(a.id, { status: "confirmed" })} disabled={isPending} className={`btn-outline !py-1 !px-3 text-xs ${busyClass}`}>
              Confirm
            </button>
          )}
          {!isCompleted && type === "confirmed" && (
            <>
              {job.viewer_can_manage && (
                <button onClick={() => updateApp(a.id, { status: "waitlist" })} disabled={isPending} className={`btn-outline !py-1 !px-3 text-xs ${busyClass}`}>Waitlist</button>
              )}

              {(job.viewer_can_manage || job.viewer_is_captain) && !a.check_in_time && a.attendance !== "absent" && (
                <button onClick={() => updateApp(a.id, { attendance_action: "check_in" })} disabled={isPending} className={`btn-outline !py-1 !px-3 text-xs ${busyClass}`}>
                  Check In
                </button>
              )}
              {(job.viewer_can_manage || job.viewer_is_captain) && a.check_in_time && !a.check_out_time && (
                <button onClick={() => updateApp(a.id, { attendance_action: "check_out" })} disabled={isPending} className={`btn-primary !py-1 !px-3 text-xs ${busyClass}`}>
                  Check Out
                </button>
              )}
              {(job.viewer_can_manage || job.viewer_is_captain) && !a.attendance && (
                <button onClick={() => updateApp(a.id, { attendance_action: "absent" })} disabled={isPending} className={`btn-outline !py-1 !px-3 text-xs !border-red-300 !text-red-500 ${busyClass}`}>
                  Absent
                </button>
              )}
              {(job.viewer_can_manage || job.viewer_is_captain) && (a.check_in_time || a.attendance) && (
                <button onClick={() => updateApp(a.id, { attendance_action: "reset" })} disabled={isPending} className={`text-xs text-muted-foreground underline underline-offset-2 ${busyClass}`}>
                  Reset
                </button>
              )}
              {job.viewer_can_manage && a.check_in_time && job?.fine_policy?.categories?.length > 0 && (
                a.fine_ratings?.length > 0 ? (
                  <button
                    onClick={() => openRateModal(a)}
                    className="flex items-center gap-1.5 text-xs px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium hover:bg-emerald-100 transition-colors"
                    title={a.fine_ratings.map(r => `${r.category}: ${r.stars}★`).join(" · ")}
                  >
                    ✓ Rated
                    <Pencil className="w-3 h-3" />
                  </button>
                ) : (
                  <button onClick={() => openRateModal(a)} className="btn-outline !py-1 !px-3 text-xs !border-amber-300 !text-amber-600">
                    Rate Employee
                  </button>
                )
              )}
              {(job.viewer_can_manage || job.viewer_can_mark_payment) && a.attendance === "present" && (
                <div className="flex items-center gap-2">
                  <div className="text-right leading-tight">
                    {a.fine_amount > 0 && (
                      <p className="text-[10px] text-muted-foreground line-through">₹{job.payment}</p>
                    )}
                    <p className="text-sm font-semibold text-foreground">
                      ₹{Math.max(0, job.payment - (a.fine_amount || 0) + (a.travel_expense || 0))}
                    </p>
                    {(a.fine_amount > 0 || a.travel_expense > 0) && (
                      <button type="button" onClick={() => setShowBreakdown(s => !s)} className="text-[10px] text-brand underline underline-offset-2">
                        {showBreakdown ? "Hide" : "Details"}
                      </button>
                    )}
                    {showBreakdown && (a.fine_amount > 0 || a.travel_expense > 0) && (
                      <div className="text-[10px] text-muted-foreground text-left mt-1 space-y-0.5 min-w-[90px]">
                        <p>Salary: ₹{job.payment}</p>
                        {a.fine_amount > 0 && <p className="text-red-500">− Fine: ₹{a.fine_amount}</p>}
                        {a.travel_expense > 0 && (
                          <div>
                            <button
                              type="button"
                              onClick={() => setShowTravelDetail(s => !s)}
                              className="text-emerald-600 flex items-center gap-0.5"
                            >
                              + Travel: ₹{a.travel_expense}
                              {showTravelDetail ? <ChevronUp className="w-2.5 h-2.5" /> : <ChevronDown className="w-2.5 h-2.5" />}
                            </button>
                            {showTravelDetail && a.travel_distance_km != null && (
                              <p className="text-muted-foreground pl-2">
                                {a.travel_distance_km} km from {a.travel_distance_source === "home" ? "Home" : "Centre"} × ₹{a.travel_rate}/km
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <label className={`flex items-center gap-1.5 cursor-pointer select-none ${busyClass}`}>
                    <span className={`text-xs font-medium ${a.payment_paid ? "text-emerald-600" : "text-muted-foreground"}`}>
                      {a.payment_paid ? "Paid" : "Mark paid"}
                    </span>
                    <button
                      type="button"
                      disabled={isPending}
                      onClick={() => togglePaid(a.id, a.payment_paid)}
                      className={`relative w-9 h-5 rounded-full transition-colors ${a.payment_paid ? "bg-emerald-500" : "bg-slate-300"}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${a.payment_paid ? "translate-x-4" : ""}`} />
                    </button>
                  </label>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
});
